import { Link } from 'react-router-dom'
import { Calculator } from 'lucide-react'
import Seo, { SITE_NAME, SITE_URL } from '@/components/Seo'
import RichText from '@/components/RichText'
import AdSlot from '@/components/AdSlot'
import SponsoredSlot from '@/components/SponsoredSlot'
import PostCard from '@/components/PostCard'
import { CATEGORIES, POSTS, formatDate, getPost } from '@/data/blog'
import NotFound from './NotFound'

function BlogPost({ slug }: { slug?: string }) {
  const post = slug ? getPost(slug) : undefined
  if (!post) return <NotFound />

  const cat = CATEGORIES.find(c => c.slug === post.category)
  const url = `${SITE_URL}/blog/${post.slug}`
  const related = POSTS.filter(p => p.slug !== post.slug)
    .sort((a, b) => Number(b.category === post.category) - Number(a.category === post.category))
    .slice(0, 2)

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: post.description,
      image: post.image,
      datePublished: post.datePublished,
      dateModified: post.dateModified,
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
      publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
        { '@type': 'ListItem', position: 3, name: post.title, item: url },
      ],
    },
  ]

  return (
    <>
      <Seo
        title={`${post.metaTitle} | Dormer Calculator`}
        description={post.description}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.image}
        schema={schema}
      />

      <div className="container-page py-8 sm:py-10">
        <nav aria-label="Breadcrumb" className="mb-5 text-sm text-steel-500">
          <Link to="/">Home</Link> <span aria-hidden="true">/</span> <Link to="/blog">Blog</Link>{' '}
          <span aria-hidden="true">/</span>{' '}
          <Link to={`/blog/category/${post.category}`}>{cat?.name}</Link>
        </nav>

        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_300px]">
          <article className="min-w-0">
            <header>
              <p className="eyebrow">{cat?.name}</p>
              <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl">{post.title}</h1>
              <p className="mt-4 text-sm text-steel-500">
                By the Dormer Calculator team &middot; Published{' '}
                <time dateTime={post.datePublished}>{formatDate(post.datePublished)}</time> &middot; Updated{' '}
                <time dateTime={post.dateModified}>{formatDate(post.dateModified)}</time> &middot; {post.readMinutes} min read
              </p>
              <img
                src={post.image}
                alt={post.imageAlt}
                width={1200}
                height={675}
                {...{ fetchpriority: 'high' }}
                className="mt-6 aspect-[16/9] w-full rounded-2xl object-cover"
              />
            </header>

            <div className="article-body mt-8">
              {post.blocks.map((b, i) => {
                if (b.type === 'h2') return <h2 key={i}>{b.text}</h2>
                if (b.type === 'p') return <p key={i}><RichText text={b.text} /></p>
                if (b.type === 'ul')
                  return (
                    <ul key={i}>
                      {b.items.map(item => (
                        <li key={item}><RichText text={item} /></li>
                      ))}
                    </ul>
                  )
                return (
                  <div key={i} className="my-8 rounded-2xl bg-ink p-6 text-white sm:p-7">
                    <h3 className="text-xl !text-white sm:text-2xl">{b.title}</h3>
                    <p className="!mb-4 mt-2 !text-base !text-blueprint-100">{b.text}</p>
                    <Link to="/calculator" className="btn btn-primary no-underline !text-ink">
                      <Calculator className="h-5 w-5" aria-hidden="true" /> Use the dormer calculator
                    </Link>
                  </div>
                )
              })}
            </div>

            <AdSlot variant="banner" />

            <p className="mt-6 rounded-xl border border-steel-200 bg-white p-4 text-sm text-steel-600">
              This article is general information, not professional advice. Prices and rules vary by location. Read our{' '}
              <Link to="/disclaimer">disclaimer</Link>.
            </p>

            <section aria-labelledby="related-heading" className="mt-12">
              <h2 id="related-heading" className="text-2xl">Keep reading</h2>
              <div className="mt-5 grid gap-6 sm:grid-cols-2">
                {related.map(p => (
                  <PostCard key={p.slug} post={p} />
                ))}
              </div>
            </section>
          </article>

          <aside aria-label="Sidebar" className="space-y-6">
            <div className="card p-5 lg:sticky lg:top-24">
              <h2 className="text-lg">Estimate your dormer</h2>
              <p className="mt-2 text-sm text-steel-600">Instant materials list and cost range from four measurements.</p>
              <Link to="/calculator" className="btn btn-primary mt-4 w-full no-underline">Open calculator</Link>
              <div className="hidden lg:block">
                <SponsoredSlot />
                <AdSlot variant="sidebar" />
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  )
}

export default BlogPost
